import {
  ApiError,
  cancelRecoveryAddressChange,
  confirmRecoveryAddressReview,
  getRecoveryAddress,
  removeRecoveryAddress,
  setRecoveryAddress,
  verifyRecoveryAddressCode,
  type RecoveryAddressResponse,
} from './api.js';
import { staleSessionMessage } from './refusals.js';

/**
 * The second address that can get somebody back in when their main mailbox is
 * gone, and the two confirmations that establish it.
 *
 * ── WHY THIS IS ON THE SECURITY SCREEN AND NOT PROFILE ───────────────
 *
 * Same argument the email address won: this is a credential, not a contact
 * detail. It is a second route into the account, gated like one, and NIST SP
 * 800-63B §4.2.3 requires that a person can view and manage it — which is a
 * requirement about this screen existing at all, not about where it sits.
 *
 * ── THE STATES ARE NOT TWO, AND NOT THREE ────────────────────────────
 *
 * `undefined` while the lookup is outstanding, `unknown` when it failed, and
 * then `none`, `pending`, or `set` — where `set` may ALSO carry a pending
 * change, because replacing an address leaves the old one working until the new
 * one is confirmed.
 *
 * Drawing "you have no recovery address" from a failed request is the same
 * mistake `loadPendingChange` refuses to make, in its most expensive form: it
 * is the reassuring direction, and here the reassurance is about the thing
 * somebody would rely on the day they are locked out.
 */

export type PendingRecovery = {
  /** The address being established. Never the one already in force. */
  recoveryEmail: string;
  requestedAt: Date | null;
  /** The NEW address has not had its six-digit code entered yet. */
  awaitingVerification: boolean;
  /** The CURRENT email address has not approved yet. */
  awaitingApproval: boolean;
};

export type RecoveryState =
  | { status: 'unknown' }
  | {
      status: 'loaded';
      /** The established address, or null when there is none yet. */
      recoveryEmail: string | null;
      /** Null when there is no address. */
      usableAt: Date | null;
      /**
       * Has the cooling-off period elapsed, so this address could recover the
       * account today? The SERVER's answer, compared against the server's
       * clock. See `RecoveryAddressResponse`.
       */
      usable: boolean;
      pending: PendingRecovery | null;
      /** Advisory only. See `RecoveryAddressResponse`. */
      sameDomainAsPrimary: boolean;
      /**
       * When the address was last confirmed as still theirs, or null if never
       * since it was established (RidgeWill/auth#383).
       *
       * Null is the ordinary state for the first year and is NOT overdue.
       * `reviewDue` is the question to render on.
       */
      reviewedAt: Date | null;
      /** When the next review falls due. Null when there is no address. */
      reviewDueAt: Date | null;
      /** The SERVER's answer, from the same rule the reminder job selects on. */
      reviewDue: boolean;
    };

function toState(out: RecoveryAddressResponse): RecoveryState {
  return {
    status: 'loaded',
    recoveryEmail: out.recoveryEmail,
    usableAt: out.usableAt ? new Date(out.usableAt) : null,
    /**
     * Defaults FALSE when the service did not send it, which reads as "not
     * ready yet".
     *
     * That is the safe direction against a service from before this shipped:
     * the screen says the address is still settling in rather than promising it
     * would work today. The opposite default would have the portal vouch for a
     * route it has no evidence about.
     */
    usable: out.usable === true,
    pending: out.pending
      ? {
          recoveryEmail: out.pending.recoveryEmail,
          requestedAt: out.pending.requestedAt ? new Date(out.pending.requestedAt) : null,
          awaitingVerification: out.pending.awaitingVerification !== false,
          awaitingApproval: out.pending.awaitingApproval !== false,
        }
      : null,
    sameDomainAsPrimary: out.sameDomainAsPrimary === true,
    reviewedAt: out.reviewedAt ? new Date(out.reviewedAt) : null,
    reviewDueAt: out.reviewDueAt ? new Date(out.reviewDueAt) : null,
    /**
     * Defaults FALSE when the service did not send it, for the same reason
     * `usable` defaults false and in the opposite direction.
     *
     * Against an auth service from before this shipped, the safe answer is to
     * show no prompt at all. The alternative — a card asking somebody to
     * confirm an address, whose button calls a route that returns 404 — is a
     * worse failure than a missing nudge, because it is one they cannot get
     * past and it appears on every load.
     */
    reviewDue: out.reviewDue === true,
  };
}

/** Read the recovery address. Never throws, and never reports `none` on a failure. */
export async function loadRecoveryAddress(): Promise<RecoveryState> {
  try {
    return toState(await getRecoveryAddress());
  } catch {
    return { status: 'unknown' };
  }
}

/**
 * The refusals every write on this screen can meet, translated once.
 *
 * ── `CONTACT_CHANGES_FROZEN` IS THE ONE WORTH READING ────────────────
 *
 * It means the account was recently recovered THROUGH this address, and both
 * contact addresses are locked for a week so that a recovery cannot be turned
 * straight into a permanent takeover. The server's message already names the
 * date, so it is passed through rather than reworded — a portal-authored
 * sentence would either drop the date or hard-code the week, and the week is a
 * server policy.
 *
 * It is the only refusal here that is not the person's fault and not fixable by
 * trying again, so it must not be worded like either.
 */
function translate(e: ApiError, action: string): string {
  if (e.code === 'CONTACT_CHANGES_FROZEN') return e.message;
  if (e.code === 'SESSION_NOT_FRESH' || e.status === 403) {
    return staleSessionMessage(action);
  }
  /**
   * The route is not there, which is deploy skew rather than a refusal. This
   * app has shipped ahead of the auth service before, and "try again in a
   * moment" would be a lie about something a person cannot affect.
   */
  if (e.status === 404) {
    return 'Recovery addresses are not available on this site just now.';
  }
  return e.message;
}

/**
 * Ask for a recovery address. Returns the new state, or a message to show.
 *
 * Never throws, for the reason `requestChange` does not: the caller is a submit
 * handler in a modal, and an unhandled rejection leaves a dialog spinning with
 * no way out but Escape.
 *
 * ── `confirmationsSent: false` IS NOT AN ERROR ───────────────────────
 *
 * It is reported so the screen can say the messages did not go out, and it is
 * the normal answer everywhere except production, because the auth service
 * refuses to mail an address outside its allowlist. Treating it as a failure
 * would make this form look broken on every environment a developer can reach.
 */
export async function requestRecoveryAddress(
  recoveryEmail: string,
  currentPassword?: string,
): Promise<{ state: RecoveryState; sent: boolean } | string> {
  try {
    const out = await setRecoveryAddress(recoveryEmail, currentPassword);
    return { state: toState(out), sent: out.confirmationsSent !== false };
  } catch (e) {
    if (e instanceof ApiError) return translate(e, 'setting a recovery address');
    return 'That could not be sent just now. Your recovery address has not changed.';
  }
}

/**
 * Enter the code mailed to the proposed address (RidgeWill/auth#393).
 *
 * Returns the new state plus whether that code finished the job, or a message
 * to show. Never throws: the caller is a submit handler, and an unhandled
 * rejection there leaves a form spinning.
 *
 * ── THE REFUSAL IS PASSED THROUGH, NOT REWORDED ──────────────────────
 *
 * The service already wrote the sentence, and it deliberately covers wrong,
 * expired, spent and never-issued with one message. Any rewording here risks
 * splitting them back apart, which would tell somebody guessing which
 * assumption was wrong.
 */
export async function enterRecoveryCode(
  code: string,
): Promise<{ state: RecoveryState; established: boolean } | string> {
  try {
    const out = await verifyRecoveryAddressCode(code);
    return { state: toState(out), established: out.established === true };
  } catch (e) {
    if (e instanceof ApiError) return translate(e, 'confirming your recovery address');
    return 'That could not be checked just now. Nothing has changed.';
  }
}

/**
 * Confirm the recovery address is still theirs (RidgeWill/auth#383).
 *
 * Returns the new state, or a message to show. Never throws, for the reason
 * every other write here does not: the caller is a click handler on a card, and
 * an unhandled rejection would leave a spinner and no explanation.
 *
 * A refusal here is unusually cheap to recover from — nothing was going to
 * change except a timestamp, and the reminder repeats — so the message says so
 * rather than implying something was lost.
 */
export async function confirmRecoveryReview(): Promise<RecoveryState | string> {
  try {
    return toState(await confirmRecoveryAddressReview());
  } catch (e) {
    if (e instanceof ApiError) return translate(e, 'confirming your recovery address');
    return 'That could not be confirmed just now. Your recovery address is unchanged, so try again in a moment.';
  }
}

/** Drop a pending change. Returns the new state, or a message to show. */
export async function cancelRecoveryChange(): Promise<RecoveryState | string> {
  try {
    return toState(await cancelRecoveryAddressChange());
  } catch (e) {
    if (e instanceof ApiError) return translate(e, 'cancelling that');
    return 'That could not be cancelled just now. Nothing has changed, so try again in a moment.';
  }
}

/** Take the recovery address off. Returns the new state, or a message to show. */
export async function dropRecoveryAddress(
  currentPassword?: string,
): Promise<RecoveryState | string> {
  try {
    return toState(await removeRecoveryAddress(currentPassword));
  } catch (e) {
    if (e instanceof ApiError) return translate(e, 'removing your recovery address');
    return 'That could not be removed just now. Nothing has changed.';
  }
}

/**
 * Would this address be the same one the account already uses?
 *
 * Refused here rather than by the server, for the reason the email form refuses
 * the same-address case itself: the shortest route to a good message is not
 * sending the request. The server refuses it too, with
 * `RECOVERY_ADDRESS_IS_PRIMARY`, so this is a nicer path to the same answer
 * rather than the only one.
 *
 * Case-insensitive and trimmed, because the service lower-cases before it
 * compares and a capitalised copy of your own address is not a different one.
 */
export function isPrimaryAddress(candidate: string, primary: string): boolean {
  const wanted = candidate.trim().toLowerCase();
  return wanted.length > 0 && wanted === primary.trim().toLowerCase();
}

/**
 * When does an address that is set become usable?
 *
 * Formatted as a date rather than a countdown. A countdown implies precision
 * the person cannot act on, and this is a week away: what they need to know is
 * "not today, and roughly when", which a date says and "6 days, 3 hours" does
 * not say better.
 *
 * Returns null when there is nothing useful to say, so the caller renders
 * nothing rather than an empty parenthesis.
 */
/**
 * The date somebody last confirmed the address, for the reassuring half of the
 * review card.
 *
 * Null when there is no address or it has never been confirmed — the caller
 * renders nothing rather than "last confirmed: never", which reads as a
 * reproach for not doing something nobody has been asked for yet.
 */
export function reviewedOn(state: RecoveryState): string | null {
  if (state.status !== 'loaded') return null;
  if (!state.recoveryEmail || !state.reviewedAt) return null;
  return state.reviewedAt.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function usableFrom(state: RecoveryState): string | null {
  if (state.status !== 'loaded') return null;
  if (!state.recoveryEmail || state.usable || !state.usableAt) return null;
  return state.usableAt.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
